import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BOOK_PRICE_DISPLAY,
  BOOK_SUBTITLE,
  EBOOK_AMAZON_URL,
  KINDLE_SELECT_MODE,
} from '../../lib/constants';
import { useCheckout } from '../../hooks/useCheckout';

export function Hero() {
  const [email, setEmail] = useState('');
  const { checkout, loading, error } = useCheckout();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) checkout(email);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-bg via-white to-sage/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-3 py-1 rounded-full bg-sage/10 text-sage-dark text-sm font-medium mb-6">
              {BOOK_SUBTITLE}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-text leading-tight">
              You're not bad with money.{' '}
              <span className="text-sage-dark">You were never taught the rules.</span>
            </h1>
            <p className="mt-6 text-lg text-text-light leading-relaxed max-w-xl">
              If you've opened your banking app and felt your stomach drop, ignored a
              credit card bill because looking at it felt too stressful, or told yourself
              &ldquo;I'll get serious next month&rdquo; more times than you want to admit,
              this book is for you.
            </p>

            {KINDLE_SELECT_MODE ? (
              <div className="mt-8 max-w-md">
                <a
                  href={EBOOK_AMAZON_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-sage text-white font-semibold hover:bg-sage-dark transition-colors whitespace-nowrap"
                >
                  Get on Kindle for {BOOK_PRICE_DISPLAY}
                </a>
                <p className="mt-3 text-xs text-text-light/60">
                  Available on Kindle and the free Kindle app.
                </p>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
                  <label htmlFor="hero-email" className="sr-only">Email address</label>
                  <input
                    id="hero-email"
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-lg border border-sage/30 bg-white text-text placeholder:text-text-light/50 focus:outline-none focus:ring-2 focus:ring-sage/50 transition"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 rounded-lg bg-sage text-white font-semibold hover:bg-sage-dark disabled:opacity-60 transition-colors whitespace-nowrap"
                  >
                    {loading ? 'Loading...' : `Buy Now for ${BOOK_PRICE_DISPLAY}`}
                  </button>
                </form>
                {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                <p className="mt-3 text-xs text-text-light/60">
                  Instant PDF download. Secure checkout via Square.
                </p>
              </>
            )}
          </motion.div>

          {/* Book cover */}
          <motion.div
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9, rotateY: -8 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 0.8, type: 'spring', stiffness: 80 }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-sage/10 rounded-2xl blur-2xl" />
              <img
                src="/MoneySkillsForYourTwenties.jpg"
                alt="Money Skills for Your Twenties book cover"
                className="relative w-72 sm:w-80 lg:w-96 rounded-lg shadow-2xl shadow-sage/20"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
